import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useTranslation } from 'react-i18next'
import {
  data,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  type MetaFunction,
  Form,
  Link,
  useActionData,
  useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Field } from '#app/components/forms.tsx'
import { SignupExistingAccount } from '#app/components/onboarding-existing-account.tsx'
import OnboardingShapes from '#app/components/onboarding-shapes.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { getInstance, getLocale } from '#app/middleware/i18next.ts'
import { findOrganizationByAccessCode } from '#app/utils/admin-api.server.ts'
import { siteTitle } from '#app/utils/global.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { Status } from '#app/utils/status.ts'

const SignupSchema = z.object({
  accessCode: z.string(),
})

export async function loader({ context }: LoaderFunctionArgs) {
  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = i18next.getFixedT(locale)
  const pageTitle = t('signup.accessCode.title')

  return { pageTitle }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()

  await checkHoneypot(formData)

  const submission = await parseWithZod(formData, {
    schema: SignupSchema.transform(async data => {
      const accessCodeResult = await findOrganizationByAccessCode(
        data.accessCode,
      )

      if (accessCodeResult.status !== Status.Success) {
        return { ...data, foundAccessCode: null }
      }

      const foundAccessCode = accessCodeResult.data

      return { ...data, foundAccessCode }
    }),
    async: true,
  })

  if (submission.status !== 'success') {
    return data(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  return { status: 'success', result: submission } as const
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const pageTitle = data?.pageTitle ?? 'Error'
  return [{ title: `${pageTitle} | ${siteTitle}` }]
}

interface Organization {
  id: string
  name: string
}

interface AccessCode {
  code: string
  organization: Organization
}

interface LastResultValue {
  foundAccessCode: AccessCode | null
}

interface LastResult {
  value: LastResultValue
}

export default function SignupRoute() {
  const actionData = useActionData<typeof action>()
  const { t } = useTranslation()
  const isPending = useIsPending()
  const [searchParams] = useSearchParams()

  const [form, fields] = useForm({
    id: 'signup-form',
    constraint: getZodConstraint(SignupSchema),
    defaultValue: {
      accessCode: searchParams.get('accessCode') ?? '',
    },
    lastResult: actionData?.result,
    onValidate({ formData }) {
      const result = parseWithZod(formData, { schema: SignupSchema })
      return result
    },
    shouldRevalidate: 'onInput',
  })

  const lastResult = actionData?.result as LastResult

  const foundAccessCode = lastResult?.value?.foundAccessCode
  const foundOrganization = foundAccessCode?.organization

  const userEnteredInvalidAccessCode = foundAccessCode === null

  return (
    <main
      id="main-content"
      className="container flex flex-col items-center justify-center pt-20 pb-32"
    >
      <div className="w-[460px]">
        <h1 className="font-poppins text-4 font-medium">
          {t('signup.accessCode.header')}
        </h1>
        <p className="text-2 mt-3">{t('signup.accessCode.instructions')}</p>

        <div className="relative">
          <Form
            className="relative mb-6 w-full"
            method="POST"
            {...getFormProps(form)}
          >
            <HoneypotInputs />
            <div className="relative">
              <OnboardingShapes
                circleClassNames="top-36"
                triangleClassNames="-top-24"
              />
              <div className="relative flex flex-col">
                <div className="dark rounded-6 border-mauve-4 bg-gradient-signup mt-6 mb-5 flex w-full flex-col border px-9 py-12">
                  <div className="relative z-10">
                    <Field
                      className="w-full"
                      labelProps={{
                        htmlFor: fields.accessCode.id,
                        children: t('signup.accessCode.fieldLabel'),
                        className: 'text-white',
                      }}
                      inputProps={{
                        ...getInputProps(fields.accessCode, { type: 'text' }),
                        autoFocus: true,
                        placeholder: t('signup.accessCode.fieldPlaceholder'),
                      }}
                      errors={fields.accessCode.errors}
                    />
                    {foundOrganization && (
                      <Icon
                        className="text-3 text-cyan-8 absolute right-2 bottom-[2.25rem]"
                        name="check"
                      />
                    )}
                    {!fields.accessCode.errors &&
                      userEnteredInvalidAccessCode && (
                        <Icon
                          className="text-1 text-error-4 absolute right-2 bottom-[2.35rem] h-3 w-3"
                          name="cross-1"
                        />
                      )}
                  </div>
                  {!fields.accessCode.errors &&
                    userEnteredInvalidAccessCode && (
                      <div className="animate-slide-down bg-error-2 text-error-4 -mt-4 flex-1 rounded-md px-3 py-3">
                        <Icon
                          className="text-5 text-error-4 h-4 w-4 flex-none"
                          name="cross-circled"
                        >
                          <div className="text-1">
                            {t('signup.accessCode.organizationNotFound')}
                          </div>
                        </Icon>
                      </div>
                    )}
                  {foundOrganization && (
                    <div className="animate-slide-down bg-cyan-11 -mt-4 rounded-md px-6 py-3 text-white">
                      <div className="text-1">
                        {t('signup.accessCode.organizationFound')}
                      </div>
                      <div className="text-2 mt-1">
                        {foundOrganization.name}
                      </div>
                    </div>
                  )}
                </div>
                {foundOrganization && (
                  <Link
                    className="link-button link-button--solid text-2 w-full"
                    to={`/signup/email?accessCode=${foundAccessCode.code}`}
                  >
                    {t('continue')}
                  </Link>
                )}
                {!foundOrganization && (
                  <StatusButton
                    className="button w-full"
                    status={isPending ? 'pending' : 'idle'}
                    type="submit"
                    disabled={isPending}
                  >
                    {t('signup.accessCode.findSubmit')}
                  </StatusButton>
                )}
              </div>
            </div>
          </Form>
          <SignupExistingAccount />
        </div>
      </div>
    </main>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
